// A repeat group's header — "Balcony × 24": its name, its count and its hours
// (SCHEMA.md "Before UAT" §4). Its surfaces are ordinary surface cards below it.
import { Copy, Trash2 } from 'lucide-react';
import type { SurfaceGroup } from '@higgins/m2q-pricing';
import { AddAction } from './add-action';
import { NumberField } from './number-field';
import { RemoveButton } from './remove-button';
import { SavedTextField } from './saved-text-field';
import { formatHours } from './surface-format';

interface SurfaceGroupHeaderProps {
  group: SurfaceGroup;
  /** The group's hours, its count applied. */
  hours: number;
  onChange: (change: Partial<Pick<SurfaceGroup, 'name' | 'count'>>) => void;
  onAddSurface: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
}

export function SurfaceGroupHeader({ group, hours, onChange, onAddSurface, onDuplicate, onRemove }: SurfaceGroupHeaderProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-t-lg border border-b-0 border-slate-300 bg-slate-50 px-3 py-2">
      <SavedTextField value={group.name} onSave={(name) => onChange({ name })} label="Group name" className="min-w-0 flex-1 bg-white px-2 text-sm font-medium" />
      <label className="flex items-center gap-1 text-sm text-slate-600">
        ×
        <span className="w-16">
          <NumberField label="How many" value={group.count} onChange={(v) => onChange({ count: v && v > 0 ? Math.round(v) : 1 })} />
        </span>
      </label>
      <span className="text-sm font-medium tabular-nums">{formatHours(hours)}</span>
      <span className="flex items-center">
        <AddAction onClick={onAddSurface} className="px-2">
          Surface
        </AddAction>
        <button type="button" onClick={onDuplicate} aria-label={`Duplicate ${group.name}`} className="flex h-11 w-11 items-center justify-center text-slate-500">
          <Copy className="h-4 w-4" />
        </button>
        <RemoveButton
          question={
            <>
              Remove <strong>{group.name}</strong> and the surfaces measured in it?
            </>
          }
          onRemove={onRemove}
          label={`Remove ${group.name}`}
          className="flex h-11 w-11 items-center justify-center text-slate-400 hover:text-red-700"
        >
          <Trash2 className="h-4 w-4" />
        </RemoveButton>
      </span>
    </div>
  );
}
